/**
 * Header chip showing the tenant's AI credit usage. Clicks through to
 * /settings/ai. Silently hides when the tenant is on BYOK (no quota) or
 * there's no subscription.
 *
 * Fetches status via React Query with 60s staleTime — the chip polls
 * gently so it stays useful without hammering the backend.
 */

import { Chip, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { aiBillingApi } from '@/api/endpoints/aiBilling';

export default function AIQuotaChip() {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['ai-billing/status'],
    queryFn: () => aiBillingApi.getStatus().then((r) => r.data),
    staleTime: 60_000,
    // Don't retry on auth failure — hide gracefully.
    retry: false,
  });

  if (!data) return null;

  // BYOK — one compact badge, no quota numbers.
  if (data.byok_enabled) {
    return (
      <Tooltip title="Bring Your Own Key is active — AI calls bill to your Anthropic account">
        <Chip
          icon={<VpnKeyOutlinedIcon />}
          label={`BYOK …${data.byok_last4 ?? '????'}`}
          size="small"
          color="info"
          variant="outlined"
          onClick={() => navigate('/settings/ai')}
          sx={{ cursor: 'pointer' }}
        />
      </Tooltip>
    );
  }

  // Unlimited plan — no number, just a stamp.
  if (data.limit === 0) {
    return (
      <Tooltip title="AI credits: unlimited on your current plan">
        <Chip
          icon={<AutoAwesomeIcon />}
          label="AI ∞"
          size="small"
          color="success"
          variant="outlined"
          onClick={() => navigate('/settings/ai')}
          sx={{ cursor: 'pointer' }}
        />
      </Tooltip>
    );
  }

  const pct = Math.min(100, Math.round((data.credits_used / data.limit) * 100));
  const color: 'default' | 'warning' | 'error' =
    pct >= 90 ? 'error' : pct >= 70 ? 'warning' : 'default';
  const remaining = Math.max(0, data.remaining);

  return (
    <Tooltip
      title={`${remaining.toLocaleString()} AI credits remaining · ${pct}% used. Click for details.`}
    >
      <Chip
        icon={<AutoAwesomeIcon />}
        label={`AI ${data.credits_used.toLocaleString()}/${data.limit.toLocaleString()}`}
        size="small"
        color={color}
        variant={pct >= 70 ? 'filled' : 'outlined'}
        onClick={() => navigate('/settings/ai')}
        sx={{ cursor: 'pointer' }}
      />
    </Tooltip>
  );
}
